import { create } from 'zustand';
import { GetMyPermissions } from '../api/api';

export interface Permission {
  resource: string;
  actions: string[];
}

interface PermissionState {
  permissions: Permission[];
  isMainAdmin: boolean;
  isLoading: boolean;
  hasFetched: boolean;
  fetchPermissions: () => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  hasAnyPermission: (resource: string, actions: string[]) => boolean;
  canAccessResource: (resource: string) => boolean;
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>((set, get) => ({
  permissions: [],
  isMainAdmin: false,
  isLoading: false,
  hasFetched: false,
  fetchPermissions: async () => {
    const state = get();
    // Prevent multiple simultaneous requests
    if (state.isLoading) {
      return;
    }
    // If we already fetched and have permissions, don't fetch again
    if (state.hasFetched && (state.permissions.length > 0 || state.isMainAdmin)) {
      return;
    }

    set({ isLoading: true });
    try {
      const { data, error } = await GetMyPermissions<{
        status: boolean;
        message: string;
        data: {
          permissions: Permission[];
          isMainAdmin: boolean;
        };
      }>();

      if (!error && data?.data) {
        set({
          permissions: data.data.permissions || [],
          isMainAdmin: data.data.isMainAdmin || false,
          isLoading: false,
          hasFetched: true,
        });
      } else {
        set({ isLoading: false, hasFetched: true });
      }
    } catch (error) {
      set({ isLoading: false, hasFetched: true });
    }
  },
  hasPermission: (resource: string, action: string) => {
    const { permissions, isMainAdmin } = get();
    if (isMainAdmin) return true;

    const permission = permissions.find((p) => p.resource === resource);
    return permission?.actions.includes(action) || false;
  },
  hasAnyPermission: (resource: string, actions: string[]) => {
    const { permissions, isMainAdmin } = get();
    if (isMainAdmin) return true;

    const permission = permissions.find((p) => p.resource === resource);
    if (!permission) return false;

    return actions.some((action) => permission.actions.includes(action));
  },
  canAccessResource: (resource: string) => {
    const { permissions, isMainAdmin } = get();
    if (isMainAdmin) return true;

    return permissions.some((p) => p.resource === resource);
  },
  reset: () => {
    set({ permissions: [], isMainAdmin: false, isLoading: false, hasFetched: false });
  },
}));

